import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import api from '../services/api';
import { getDeviceId } from '../utils/deviceId';
import { EyeIcon, EyeOffIcon } from '../components/PasswordToggleIcon';

export default function LoginPage() {
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const inactivityLogout = searchParams.get('reason') === 'inactivity';

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const deviceToken = localStorage.getItem('device_token');
      const result = await login(email, password, deviceToken);

      if (result?.requires_password_change) {
        navigate('/force-password-change', {
          state: { tempToken: result.temp_token },
        });
        return;
      }

      if (result?.requires_mfa_enrollment) {
        navigate('/enroll-mfa', {
          state: { enrollmentToken: result.enrollment_token },
        });
        return;
      }

      if (result?.requires_mfa) {
        setMfaToken(result.mfa_token);
        setPhase('mfa');
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/mfa/verify', {
        mfa_token: mfaToken,
        totp_code: totpCode,
        trust_device: trustDevice,
        device_id: getDeviceId(),
      });

      const { access_token, refresh_token, user, device_token } = response.data;
      const tokenToStore = trustDevice ? device_token : null;
      completeLogin(access_token, refresh_token, user, tokenToStore);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Invalid code. Please try again.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setPhase('credentials');
    setMfaToken('');
    setTotpCode('');
    setTrustDevice(false);
    setError('');
  };

  const handleUseRecoveryCode = () => {
    navigate('/mfa-recovery', { state: { mfaToken } });
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Nephr&#333;s</h1>
          <p style={styles.subtitle}>CKD Care Coordination</p>
        </div>

        {inactivityLogout && (
          <div style={styles.inactivityBanner}>
            You were logged out due to inactivity. Please log in again.
          </div>
        )}

        {error && (
          <div style={styles.errorBanner}>{error}</div>
        )}

        {/* Phase 1 — credentials */}
        {phase === 'credentials' && (
          <form onSubmit={handleCredentialsSubmit} style={styles.form}>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                autoComplete="email"
                autoFocus
                required
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="password">Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...styles.input, ...styles.passwordInput }}
                  autoComplete="current-password"
                  required
                />
                {/* Eye toggle is an inline icon control — plain button intentional */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.showHideButton}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} style={{ marginTop: '4px', width: '100%' }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        )}

        {/* Phase 2 — MFA */}
        {phase === 'mfa' && (
          <form onSubmit={handleMfaSubmit} style={styles.form}>
            <p style={styles.mfaPrompt}>
              Enter the 6-digit code from your authenticator app.
            </p>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="totp">Authentication Code</label>
              <input
                id="totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                style={{ ...styles.input, letterSpacing: '0.2em', textAlign: 'center' }}
                autoFocus
                required
              />
            </div>

            <Button type="submit" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Verifying...' : 'Verify'}
            </Button>

            <label style={styles.trustDeviceLabel}>
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={e => setTrustDevice(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Trust this device
            </label>

            <Button variant="ghost" type="button" onClick={handleUseRecoveryCode}
              style={{ alignSelf: 'center' }}>
              Use a recovery code instead
            </Button>

            <Button variant="ghost" type="button" onClick={handleBackToLogin}
              style={{ alignSelf: 'center' }}>
              Back to login
            </Button>
          </form>
        )}

        {phase === 'credentials' && (
          <div style={styles.footer}>
            <Button variant="ghost" onClick={() => navigate('/forgot-password')}>
              Forgot password?
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'var(--color-bg-page)', fontFamily: 'var(--font-family-base)',
  },
  card: {
    backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)', padding: '48px', width: '100%', maxWidth: '400px',
  },
  header: { textAlign: 'center', marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-nav)', margin: '0 0 4px 0', letterSpacing: '-0.5px', fontFamily: 'var(--font-family-base)' },
  subtitle: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  inactivityBanner: { backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#92400e', marginBottom: '20px', fontFamily: 'var(--font-family-base)' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '20px', fontFamily: 'var(--font-family-base)' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  input: { padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', outline: 'none', transition: 'border-color var(--transition-fast)', fontFamily: 'var(--font-family-base)' },
  passwordWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  passwordInput: { flex: 1, paddingRight: '40px' },
  showHideButton: { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', display: 'flex', alignItems: 'center' },
  footer: { textAlign: 'center', marginTop: '24px' },
  mfaPrompt: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0', textAlign: 'center', fontFamily: 'var(--font-family-base)' },
  trustDeviceLabel: { display: 'flex', alignItems: 'center', fontSize: '13px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-family-base)' },
};